import React, { useState } from 'react';
import {
  Printer,
  Download,
  X,
  CheckCircle2,
  Award,
  FileText,
  ShieldCheck,
  CreditCard,
  User,
  Calendar,
  Clock,
  MapPin,
  Building2
} from 'lucide-react';
import './EnglishAdmissionsDocuments.css';

function formatFormalDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return dateStr;
  }
}

function formatCurrencyIDR(amount) {
  const num = Number(amount || 0);
  return 'IDR ' + num.toLocaleString('en-US');
}

/**
 * 1. OFFICIAL LETTER OF ACCEPTANCE (LoA)
 * Formal English Admission Acceptance Letter
 */
export function LetterOfAcceptanceModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const parentName = app.parent_info?.full_name || app.applicant_name || 'Parent / Guardian';
  const regNo = app.registration_no || `REG-PHS-${app.id || '0001'}`;
  const loaNumber = app.loa_number || `LOA-PHS/2026/${app.id || '0001'}`;
  const issuedDate = formatFormalDate(app.loa_issued_at || app.updated_at);
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';
  const campus = app.campus_location || 'Simprug - Jl. Pos Pengumben Lama No.13';
  const levelName = app.level || app.level_name || 'Sekolah Permata Harapan';

  const officerName = app.admissions_officer_name || 'Ivana';
  const officerRole = 'Admissions Officer';
  const principalName = app.school_principal_name || (['preschool', 'kindergarten'].includes(String(app.level_code || '').toLowerCase()) ? 'Ratna Sari, S.Pd., M.Pd' : 'Asnah Roidah Yulianti, S.Si., M.Pd');
  const principalRole = app.school_principal_title || 'School Principal';

  const inv = app.invoice || {};
  const regFee = Number(inv.registration_fee || 0);
  const admissionFee = Number(inv.admission_fee || 0);
  const devFee = Number(inv.development_fee || 0);
  const booksFee = Number(inv.books_activity_fee || 0);
  const tuitionFee = Number(inv.tuition_fee || 0);
  const discountAmount = Number(inv.discount_amount || app.discount_amount || 0);
  const totalPayable = Number(inv.total_payable || 0);

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Letter_of_Acceptance_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-loa');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Letter_of_Acceptance_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <Award size={18} />
            <span>Letter of Acceptance (LoA) - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-loa">
            {/* Header */}
            <div className="ead-header">
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className="ead-logo-box">
                    <img
                      src="/logo_phs.png"
                      alt="Sekolah Permata Harapan Logo"
                      className="ead-school-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                  </div>
                  <div className="ead-brand-text">
                    <h1>Sekolah Permata Harapan</h1>
                    <p>Admissions &amp; Academic Enrollment Directorate</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Sekolah Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Phone: (021) 5890 1100 • Email: admissions@permataharapansecondary.sch.id<br />
                  Website: https://permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Reference & Date */}
            <div className="ead-meta-bar">
              <div><strong>Reference No:</strong> {loaNumber}</div>
              <div><strong>Date of Issuance:</strong> {issuedDate}</div>
            </div>

            {/* Document Title */}
            <div className="ead-doc-title-block">
              <h2 className="ead-doc-title">Official Letter of Acceptance</h2>
              <div className="ead-doc-subtitle">Academic Year {academicYear} Admission Cycle</div>
            </div>

            {/* Recipient */}
            <div className="ead-salutation">
              To the Parents / Guardians of: <strong>{childName}</strong><br />
              Registration Number: <strong>{regNo}</strong>
            </div>

            <p className="ead-paragraph">
              Dear Mr./Mrs. {parentName},
            </p>

            <p className="ead-paragraph">
              On behalf of the Board of Governors, Faculty, and Admissions Committee of Sekolah Permata Harapan, it is our distinct pleasure to officially inform you that your child, <strong>{childName}</strong>, has satisfactorily fulfilled the institutional assessment standards and has been <strong>OFFICIALLY ACCEPTED</strong> for admission into Sekolah Permata Harapan for the <strong>{academicYear}</strong> academic cycle.
            </p>

            {/* Admission Specifics Box */}
            <div className="ead-highlight-box">
              <div className="ead-grid-2">
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Accepted Student</span>
                  <span className="ead-grid-value">{childName}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Admission Level &amp; Grade Target</span>
                  <span className="ead-grid-value">{gradeTarget} ({levelName})</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Campus Placement</span>
                  <span className="ead-grid-value">{campus}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Academic Intake</span>
                  <span className="ead-grid-value">Academic Year {academicYear}</span>
                </div>
              </div>
            </div>

            {app.test_score !== null && app.test_score !== undefined && (
              <p className="ead-paragraph" style={{ marginTop: '16px' }}>
                <strong>Assessment Result:</strong> Passed entrance evaluation with an overall score of{' '}
                <span style={{ color: '#059669', fontWeight: 700 }}>{app.test_score} / 100</span>.
                {app.test_evaluation_notes && (
                  <span style={{ fontStyle: 'italic', display: 'block', marginTop: '4px', color: '#475569' }}>
                    Note: "{app.test_evaluation_notes}"
                  </span>
                )}
              </p>
            )}

            {/* Fee Schedule Summary */}
            {totalPayable > 0 && (
              <>
                <div className="ead-doc-subtitle" style={{ textAlign: 'left', margin: '20px 0 8px', fontSize: '0.85rem' }}>
                  SCHEDULE OF ADMISSION FEES &amp; SETTLEMENT (IDR)
                </div>
                <table className="ead-table">
                  <thead>
                    <tr>
                      <th style={{ width: '45px' }}>No</th>
                      <th>Fee Component</th>
                      <th className="text-right" style={{ width: '180px' }}>Amount (IDR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admissionFee > 0 && (
                      <tr>
                        <td>1</td>
                        <td>Admission &amp; Registration Fee</td>
                        <td className="text-right">{formatCurrencyIDR(admissionFee + regFee)}</td>
                      </tr>
                    )}
                    {devFee > 0 && (
                      <tr>
                        <td>2</td>
                        <td>Development Fee (Uang Pangkal)</td>
                        <td className="text-right">{formatCurrencyIDR(devFee)}</td>
                      </tr>
                    )}
                    {booksFee > 0 && (
                      <tr>
                        <td>3</td>
                        <td>Books, Uniform &amp; Learning Activity Fee</td>
                        <td className="text-right">{formatCurrencyIDR(booksFee)}</td>
                      </tr>
                    )}
                    {tuitionFee > 0 && (
                      <tr>
                        <td>4</td>
                        <td>First Month Tuition Fee (SPP)</td>
                        <td className="text-right">{formatCurrencyIDR(tuitionFee)}</td>
                      </tr>
                    )}
                    {discountAmount > 0 && (
                      <tr className="ead-discount-row">
                        <td>-</td>
                        <td>Official Discount / Promotion ({inv.discount_label || app.promo_code_used || 'Approved Incentive'})</td>
                        <td className="text-right">-{formatCurrencyIDR(discountAmount)}</td>
                      </tr>
                    )}
                    <tr className="ead-total-row">
                      <td colSpan="2">TOTAL ADMISSION INVESTMENT</td>
                      <td className="text-right">{formatCurrencyIDR(totalPayable)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            <p className="ead-paragraph" style={{ fontSize: '0.85rem', color: '#475569' }}>
              To finalize admission placement and reserve your child's student seat, please complete the enrollment confirmation through our Parent Portal within the designated settlement timeframe.
            </p>

            {/* Signatures */}
            <div className="ead-signature-section">
              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-official-badge">
                    <ShieldCheck size={14} />
                    <span>Admissions Office</span>
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{officerName}</p>
                <p className="ead-sig-role">{officerRole} • Sekolah Permata Harapan</p>
              </div>

              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-sig-stamp">
                    SEKOLAH<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{principalName}</p>
                <p className="ead-sig-role">{principalRole} • Sekolah Permata Harapan</p>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note">
              This is an official document generated by the Admissions Portal of Sekolah Permata Harapan. Validation may be verified at the School Admissions Office.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 2. OFFICIAL CERTIFICATE OF ADMISSION & STUDENT ENROLLMENT (Bukti Siswa Resmi)
 * Formal English Certificate confirming the child is a registered student with NIS
 */
export function OfficialEnrollmentCertificateModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const nis = app.student_id_number || `PHS-2026-${String(app.id || 1).padStart(4, '0')}`;
  const enrolledDate = formatFormalDate(app.enrolled_at || app.payment_verified_at || app.updated_at);
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';
  const levelName = app.level || app.level_name || 'Sekolah Permata Harapan';
  const campus = app.campus_location || 'Simprug - Jl. Pos Pengumben Lama No.13';

  const officerName = app.admissions_officer_name || 'Ivana';
  const officerRole = 'Admissions Officer';
  const principalName = app.school_principal_name || (['preschool', 'kindergarten'].includes(String(app.level_code || '').toLowerCase()) ? 'Ratna Sari, S.Pd., M.Pd' : 'Asnah Roidah Yulianti, S.Si., M.Pd');
  const principalRole = app.school_principal_title || 'School Principal';

  const formData = app.form_data || {};
  const birthPlace = app.birth_place || formData.birth_place || '-';
  const birthDate = formatFormalDate(app.birth_date || formData.birth_date);
  const gender = app.gender || formData.gender || '-';
  const nationality = app.nationality || formData.nationality || 'Indonesian (WNI)';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Official_Student_Enrollment_Certificate_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-cert');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Official_Student_Enrollment_Certificate_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <CheckCircle2 size={18} />
            <span>Official Student Enrollment Certificate - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-cert" style={{ border: '2px solid #0f172a', padding: '44px 50px' }}>
            {/* Header */}
            <div className="ead-header" style={{ borderBottom: '3px double #0f172a' }}>
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className="ead-logo-box">
                    <img
                      src="/logo_phs.png"
                      alt="Sekolah Permata Harapan Logo"
                      className="ead-school-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                  </div>
                  <div className="ead-brand-text">
                    <h1 style={{ letterSpacing: '0.05em' }}>Sekolah Permata Harapan</h1>
                    <p>Office of the Registrar &amp; Student Administration</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Sekolah Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Phone: (021) 5890 1100 • Email: admissions@permataharapansecondary.sch.id<br />
                  Website: https://permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Document Title */}
            <div className="ead-doc-title-block" style={{ margin: '24px 0 28px' }}>
              <div style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '0.8rem', color: '#64748b', fontWeight: 700 }}>
                Official Academic Credential
              </div>
              <h2 className="ead-doc-title" style={{ fontSize: '1.6rem', marginTop: '6px' }}>
                Certificate of Official Enrollment
              </h2>
              <div className="ead-doc-subtitle" style={{ fontSize: '0.9rem', color: '#334155' }}>
                This is to certify that the individual named below is a duly enrolled student of Sekolah Permata Harapan
              </div>
            </div>

            {/* Certificate Body */}
            <p className="ead-paragraph" style={{ textAlign: 'center', fontSize: '0.95rem', margin: '0 0 20px' }}>
              The Office of Admissions &amp; Registrar hereby certifies and confirms that:
            </p>

            <div className="ead-highlight-box" style={{ background: '#fdfdfe', border: '2px solid #0f172a', padding: '24px 28px' }}>
              <div className="ead-grid-2">
                <div className="ead-grid-item" style={{ gridColumn: 'span 2', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                  <span className="ead-grid-label">Full Name of Student</span>
                  <span className="ead-grid-value" style={{ fontSize: '1.25rem', color: '#0f172a' }}>{childName}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Student Identification Number (NIS)</span>
                  <span className="ead-grid-value" style={{ color: '#2563eb', fontSize: '1.1rem' }}>{nis}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Academic Program &amp; Level</span>
                  <span className="ead-grid-value">{gradeTarget} ({levelName})</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Place &amp; Date of Birth</span>
                  <span className="ead-grid-value">{birthPlace}, {birthDate}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Gender &amp; Nationality</span>
                  <span className="ead-grid-value">{gender} / {nationality}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Enrolled Campus Location</span>
                  <span className="ead-grid-value">{campus}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Academic Year</span>
                  <span className="ead-grid-value">{academicYear}</span>
                </div>
              </div>
            </div>

            <p className="ead-paragraph" style={{ marginTop: '20px' }}>
              The student named above has completed all required entrance evaluations, submitted verified documentation, and fulfilled all admission prerequisites and institutional matriculation protocols. In witness whereof, the student is officially admitted and listed in the active registry of Sekolah Permata Harapan with all associated academic rights and institutional privileges.
            </p>

            <div className="ead-meta-bar" style={{ marginTop: '24px', border: 'none', background: '#f8fafc', padding: '8px 16px', borderRadius: '4px' }}>
              <div><strong>Effective Date of Enrollment:</strong> {enrolledDate}</div>
              <div><strong>Registry Status:</strong> <span style={{ color: '#059669', fontWeight: 700 }}>Active &amp; Verified</span></div>
            </div>

            {/* Signatures */}
            <div className="ead-signature-section" style={{ marginTop: '32px' }}>
              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-official-badge">
                    <ShieldCheck size={14} />
                    <span>Academic Registrar</span>
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{officerName}</p>
                <p className="ead-sig-role">{officerRole} • Office of the Registrar</p>
              </div>

              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-sig-stamp">
                    SEKOLAH<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{principalName}</p>
                <p className="ead-sig-role">{principalRole} • Sekolah Permata Harapan</p>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note">
              This certificate confirms official student status and is recognized for official transfer, consular, and academic identification purposes.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 3. OFFICIAL PAYMENT RECEIPT & STATEMENT OF ACCOUNT (Kwitansi Pembayaran Resmi)
 * Formal English Receipt for Tuition & Development Fees (No Allocation / Period column)
 */
export function OfficialPaymentReceiptModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Student Candidate';
  const parentName = app.parent_info?.full_name || app.applicant_name || 'Parent / Guardian';
  const nis = app.student_id_number || `PHS-2026-${String(app.id || 1).padStart(4, '0')}`;
  const receiptNo = app.receipt_number || app.invoice?.receipt_no || `RCP-PH/2026/${app.id || '0001'}`;
  const verifiedDate = formatFormalDate(app.payment_verified_at || app.payment_submitted_at || app.updated_at);
  const paymentMethod = app.payment_method || 'Bank Transfer (BCA - Bank Central Asia)';
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Primary School';

  const officerName = app.admissions_officer_name || 'Ivana';
  const financeName = app.finance_pic_name || 'Zahra, S.E.';
  const financeRole = app.finance_pic_title || 'Finance & Treasury PIC';

  const inv = app.invoice || {};
  const regFee = Number(inv.registration_fee || 0);
  const admissionFee = Number(inv.admission_fee || 0);
  const devFee = Number(inv.development_fee || 0);
  const booksFee = Number(inv.books_activity_fee || 0);
  const tuitionFee = Number(inv.tuition_fee || 0);
  const discountAmount = Number(inv.discount_amount || app.discount_amount || 0);
  const totalPayable = Number(inv.total_payable || 0);
  const amountPaid = Number(app.payment_amount_paid || inv.amount_paid || totalPayable);

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Official_Payment_Receipt_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-receipt');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Official_Payment_Receipt_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <CreditCard size={18} />
            <span>Official Payment Receipt &amp; Invoice - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-receipt">
            {/* Header */}
            <div className="ead-header">
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className="ead-logo-box">
                    <img
                      src="/logo_phs.png"
                      alt="Sekolah Permata Harapan Logo"
                      className="ead-school-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                  </div>
                  <div className="ead-brand-text">
                    <h1>Sekolah Permata Harapan</h1>
                    <p>Bursar &amp; Student Financial Services Directorate</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Yayasan Pendidikan Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Tax ID / NPWP: 01.345.678.9-082.000 • Phone: (021) 5890 1100<br />
                  Email: admissions@permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Reference Bar */}
            <div className="ead-meta-bar">
              <div><strong>Receipt Number:</strong> <span style={{ color: '#2563eb' }}>{receiptNo}</span></div>
              <div><strong>Payment Verified Date:</strong> {verifiedDate}</div>
            </div>

            {/* Title */}
            <div className="ead-doc-title-block">
              <h2 className="ead-doc-title">Official Payment Receipt</h2>
              <div className="ead-doc-subtitle">Statement of Academic Account &amp; Settlement Confirmation</div>
            </div>

            {/* Account Details Box */}
            <div className="ead-highlight-box" style={{ background: '#f8fafc' }}>
              <div className="ead-grid-2">
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Received From (Payor)</span>
                  <span className="ead-grid-value">{parentName}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Student Name &amp; NIS</span>
                  <span className="ead-grid-value">{childName} (NIS: {nis})</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Program &amp; Academic Year</span>
                  <span className="ead-grid-value">{gradeTarget} - TA {academicYear}</span>
                </div>
                <div className="ead-grid-item">
                  <span className="ead-grid-label">Settlement Channel</span>
                  <span className="ead-grid-value">{paymentMethod}</span>
                </div>
              </div>
            </div>

            {/* Itemized Table without Allocation / Period */}
            <table className="ead-table">
              <thead>
                <tr>
                  <th style={{ width: '45px' }}>No</th>
                  <th>Fee Description</th>
                  <th className="text-right" style={{ width: '180px' }}>Amount (IDR)</th>
                </tr>
              </thead>
              <tbody>
                {admissionFee > 0 && (
                  <tr>
                    <td>1</td>
                    <td>Admission &amp; Matriculation Fee</td>
                    <td className="text-right">{formatCurrencyIDR(admissionFee + regFee)}</td>
                  </tr>
                )}
                {devFee > 0 && (
                  <tr>
                    <td>2</td>
                    <td>Development Fee (Uang Pangkal)</td>
                    <td className="text-right">{formatCurrencyIDR(devFee)}</td>
                  </tr>
                )}
                {booksFee > 0 && (
                  <tr>
                    <td>3</td>
                    <td>Books, Learning Materials &amp; Activity Fee</td>
                    <td className="text-right">{formatCurrencyIDR(booksFee)}</td>
                  </tr>
                )}
                {tuitionFee > 0 && (
                  <tr>
                    <td>4</td>
                    <td>Monthly Tuition Fee (SPP)</td>
                    <td className="text-right">{formatCurrencyIDR(tuitionFee)}</td>
                  </tr>
                )}
                {discountAmount > 0 && (
                  <tr className="ead-discount-row">
                    <td>-</td>
                    <td>Approved Institutional Discount / Voucher ({inv.discount_label || app.promo_code_used || 'Approved Incentive'})</td>
                    <td className="text-right">-{formatCurrencyIDR(discountAmount)}</td>
                  </tr>
                )}
                <tr className="ead-total-row">
                  <td colSpan="2">TOTAL AMOUNT PAID &amp; VERIFIED</td>
                  <td className="text-right" style={{ color: '#059669', fontSize: '1.05rem' }}>
                    {formatCurrencyIDR(amountPaid > 0 ? amountPaid : totalPayable)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Payment Status Stamp */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0', background: '#ecfdf5', padding: '12px 18px', borderRadius: '6px', border: '1px solid #a7f3d0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle2 size={22} color="#059669" />
                <div>
                  <strong style={{ color: '#065f46', fontSize: '0.9rem' }}>STATUS: PAYMENT VERIFIED &amp; CLEARED</strong><br />
                  <span style={{ fontSize: '0.78rem', color: '#047857' }}>
                    Funds credited to Yayasan Pendidikan Permata Harapan (BCA Account 548-098-7788)
                  </span>
                </div>
              </div>
              <div style={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 800, color: '#059669', border: '2px solid #059669', padding: '4px 10px', borderRadius: '4px', letterSpacing: '0.08em' }}>
                OFFICIAL PAID
              </div>
            </div>

            <p className="ead-paragraph" style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic', margin: '14px 0 0' }}>
              * Important Institutional Notice: In accordance with standard school admissions regulations, all payments made to Yayasan Pendidikan Permata Harapan are strictly non-refundable and non-transferable under any circumstances once processed.
            </p>

            {/* Signatures */}
            <div className="ead-signature-section">
              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-official-badge">
                    <ShieldCheck size={14} />
                    <span>Admissions Verification</span>
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{officerName}</p>
                <p className="ead-sig-role">Admissions &amp; Verification Officer</p>
              </div>

              <div className="ead-sig-col">
                <div className="ead-sig-space">
                  <div className="ead-sig-stamp">
                    YAYASAN<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-sig-line" />
                <p className="ead-sig-name">{financeName}</p>
                <p className="ead-sig-role">{financeRole} • Yayasan Pendidikan Permata Harapan</p>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note">
              This receipt is computer-generated and officially acknowledged by the Bursar Office of Sekolah Permata Harapan.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 4. KARTU TANDA PESERTA TES & OBSERVASI PPDB (OFFICIAL EXAMINATION PASS)
 * Official Admission Test & Observation Card for offline entrance assessment
 */
export function ExaminationPassModal({ app, onClose }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  if (!app) return null;

  const childName = app.child_full_name || app.student_name || app.name || 'Calon Siswa';
  const regNo = app.registration_no || `REG-PHS-${app.id || '0001'}`;
  const periodName = app.period_name || 'Gelombang 1 (Early Bird)';
  const academicYear = app.academic_year || '2026/2027';
  const gradeTarget = app.grade_target || app.level_name || 'Secondary 1 (Grade 7)';
  const levelName = app.level_name || app.level || 'Secondary School / SMP';

  const testSched = app.test_schedule || {};
  const slotName = testSched.slot_name || app.test_slot_name || 'Sesi Tes & Observasi Masuk';
  const testDate = formatFormalDate(testSched.test_date || app.test_date);
  const testTime = testSched.start_time && testSched.end_time
    ? `${testSched.start_time} - ${testSched.end_time} WIB`
    : app.test_time || '09:00 - 11:30 WIB';
  const campus = testSched.campus_location || app.campus_location || 'Kampus Simprug - Jl. Pos Pengumben Lama No.13';
  const room = testSched.room_name || app.room_name || 'Ruang Observasi & Akademik';
  const assessmentTitle = testSched.assessment_title || 'Cambridge Entrance Assessment & Academic Interview';
  const assessmentSubjects = testSched.assessment_subjects || 'English Proficiency, Mathematics, Integrated Science & Family Interview';

  const formData = app.form_data || {};
  const birthPlace = app.birth_place || formData.birth_place || 'Jakarta';
  const birthDate = formatFormalDate(app.birth_date || formData.birth_date);
  const gender = (app.gender || formData.gender || '').toLowerCase() === 'female' ? 'Perempuan' : 'Laki-laki';
  const previousSchool = app.previous_school || formData.previous_school || '-';
  const parentName = app.father_name || app.mother_name || app.parent_info?.full_name || app.applicant_name || 'Orang Tua / Wali';
  const parentPhone = app.parent_phone || formData.parent_phone || '-';
  const photoUrl = app.student_photo_url || app.pass_photo_url || app.photo_url || formData.student_photo_url;
  const officerName = app.admissions_officer_name || 'Panitia Penerimaan Siswa Baru';

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `Kartu_Peserta_Tes_${childName.replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleDownloadPdf = async () => {
    const sheetEl = document.querySelector('.ead-sheet-exam');
    if (!sheetEl) return;
    try {
      setDownloadingPdf(true);
      const html2pdf = (await import('html2pdf.js')).default;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Kartu_Peserta_Tes_${childName.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(sheetEl).save();
    } catch (err) {
      console.error('PDF error, fallback to print:', err);
      handlePrint();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="ead-modal-overlay" onClick={onClose}>
      <div className="ead-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ead-modal-toolbar">
          <div className="ead-toolbar-title">
            <Award size={18} />
            <span>Kartu Tanda Peserta Tes &amp; Observasi PPDB - {childName}</span>
          </div>
          <div className="ead-toolbar-actions">
            <button
              type="button"
              className="ead-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <Download size={15} />
              <span>{downloadingPdf ? 'Menyimpan PDF...' : 'Simpan / Unduh PDF (.pdf)'}</span>
            </button>
            <button type="button" className="ead-btn-print" onClick={handlePrint}>
              <Printer size={15} />
              <span>Cetak Dokumen</span>
            </button>
            <button type="button" className="ead-btn-close" onClick={onClose} aria-label="Tutup">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ead-paper-scroll">
          <div className="ead-sheet ead-sheet-exam">
            {/* Header Kop Surat */}
            <div className="ead-header" style={{ paddingBottom: 12 }}>
              <div className="ead-header-inner">
                <div className="ead-brand-col">
                  <div className="ead-logo-box">
                    <img
                      src="/logo_phs.png"
                      alt="Sekolah Permata Harapan Logo"
                      className="ead-school-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="ead-logo-badge" style={{ display: 'none' }}>PHS</div>
                  </div>
                  <div className="ead-brand-text">
                    <h1>Sekolah Permata Harapan</h1>
                    <p>Panitia Penerimaan Peserta Didik Baru (PPDB)</p>
                  </div>
                </div>
                <div className="ead-contact-col">
                  <strong>Sekolah Permata Harapan</strong><br />
                  Jl. Pos Pengumben Lama No. 13, Srengseng, Kembangan, Jakarta Barat 11630<br />
                  Phone: (021) 5890 1100 • Email: admissions@permataharapansecondary.sch.id<br />
                  Website: https://permataharapansecondary.sch.id
                </div>
              </div>
            </div>

            {/* Document Title Banner */}
            <div className="ead-exam-title-banner">
              <h2>KARTU TANDA PESERTA SELEKSI MASUK &amp; OBSERVASI</h2>
              <div className="ead-exam-subtitle">
                Tahun Pelajaran {academicYear} • {levelName}
              </div>
            </div>

            {/* Two-Column Candidate Profile & Photo Section */}
            <div className="ead-exam-profile-grid">
              <div className="ead-exam-photo-col">
                <div className="ead-exam-photo-card">
                  {photoUrl ? (
                    <img src={photoUrl} alt={childName} className="ead-exam-photo-img" />
                  ) : (
                    <div className="ead-exam-photo-placeholder">
                      <User size={36} color="#94a3b8" />
                      <span>Pas Foto 4x3 Calon Siswa</span>
                    </div>
                  )}
                </div>
                <div className="ead-exam-reg-badge">
                  <small>Nomor Registrasi / Peserta</small>
                  <strong>{regNo}</strong>
                </div>
                <div className="ead-exam-period-badge">
                  {periodName}
                </div>
              </div>

              <div className="ead-exam-info-col">
                <table className="ead-exam-table">
                  <tbody>
                    <tr>
                      <td className="ead-col-label">Nama Lengkap Siswa</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val"><strong>{childName}</strong></td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Jenjang &amp; Kelas Dituju</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{levelName} — {gradeTarget}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Tempat, Tanggal Lahir</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{birthPlace}, {birthDate}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Jenis Kelamin</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{gender}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Asal Sekolah</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{previousSchool}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Nama Orang Tua / Wali</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{parentName}</td>
                    </tr>
                    <tr>
                      <td className="ead-col-label">Kontak / No. WhatsApp</td>
                      <td className="ead-col-colon">:</td>
                      <td className="ead-col-val">{parentPhone}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Test Schedule & Location Box */}
            <div className="ead-exam-sched-card">
              <div className="ead-exam-sched-head">
                <Calendar size={16} />
                <span>Jadwal &amp; Lokasi Pelaksanaan Tes Offline</span>
              </div>
              <div className="ead-exam-sched-grid">
                <div>
                  <small>Hari &amp; Tanggal</small>
                  <strong>{testDate}</strong>
                </div>
                <div>
                  <small>Waktu Pelaksanaan</small>
                  <strong>{testTime}</strong>
                </div>
                <div>
                  <small>Sesi Tes</small>
                  <strong>{slotName}</strong>
                </div>
                <div>
                  <small>Lokasi Kampus &amp; Ruangan</small>
                  <strong>{campus} ({room})</strong>
                </div>
              </div>
            </div>

            {/* Assessment Focus */}
            <div className="ead-exam-subjects-card">
              <strong>Fokus Materi Asesmen:</strong> {assessmentTitle}
              <div style={{ marginTop: 3, color: '#334155', fontSize: '0.8rem' }}>
                {assessmentSubjects}
              </div>
            </div>

            {/* Exam Rules & Instructions */}
            <div className="ead-exam-rules-card">
              <h4>Tata Tertib &amp; Ketentuan Peserta Tes:</h4>
              <ol className="ead-exam-rules-list">
                <li>Peserta wajib hadir di lokasi kampus Sekolah Permata Harapan minimal 15 (lima belas) menit sebelum waktu ujian dimulai.</li>
                <li>Membawa Kartu Tanda Peserta ini (telah dicetak) dan alat tulis lengkap (pensil 2B, penghapus, penggaris, dan pulpen).</li>
                <li>Mengenakan pakaian seragam sekolah asal atau pakaian rapi, sopan, berkerah, dan bersepatu.</li>
                <li>Kartu Tanda Peserta ini wajib ditunjukkan kepada Petugas Registrasi &amp; Pengawas Ruang pada saat verifikasi kehadiran.</li>
                <li>Orang tua / wali pendamping wajib menunggu di area drop-off / ruang tunggu yang telah disediakan oleh panitia.</li>
              </ol>
            </div>

            {/* Signatures & QR Code Verification */}
            <div className="ead-exam-sig-row">
              <div className="ead-exam-sig-col">
                <p className="ead-exam-sig-title">Tanda Tangan Calon Siswa,</p>
                <div className="ead-exam-sig-space" />
                <div className="ead-exam-sig-line" />
                <p className="ead-exam-sig-name">{childName}</p>
                <small className="ead-exam-sig-note">Tanda tangan pada saat hadir di ruang tes</small>
              </div>

              <div className="ead-exam-qr-col">
                <div className="ead-qr-frame">
                  <svg width="84" height="84" viewBox="0 0 100 100">
                    <rect width="100" height="100" fill="#ffffff" />
                    <rect x="10" y="10" width="24" height="24" fill="#0f172a" />
                    <rect x="14" y="14" width="16" height="16" fill="#ffffff" />
                    <rect x="18" y="18" width="8" height="8" fill="#0f172a" />
                    <rect x="66" y="10" width="24" height="24" fill="#0f172a" />
                    <rect x="70" y="14" width="16" height="16" fill="#ffffff" />
                    <rect x="74" y="18" width="8" height="8" fill="#0f172a" />
                    <rect x="10" y="66" width="24" height="24" fill="#0f172a" />
                    <rect x="14" y="70" width="16" height="16" fill="#ffffff" />
                    <rect x="18" y="74" width="8" height="8" fill="#0f172a" />
                    <rect x="42" y="14" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="24" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="34" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="44" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="54" width="6" height="6" fill="#0f172a" />
                    <rect x="52" y="64" width="6" height="6" fill="#0f172a" />
                    <rect x="42" y="74" width="6" height="6" fill="#0f172a" />
                    <rect x="66" y="44" width="6" height="6" fill="#0f172a" />
                    <rect x="76" y="54" width="6" height="6" fill="#0f172a" />
                    <rect x="86" y="64" width="6" height="6" fill="#0f172a" />
                    <rect x="66" y="74" width="6" height="6" fill="#0f172a" />
                    <rect x="76" y="84" width="6" height="6" fill="#0f172a" />
                  </svg>
                </div>
                <span className="ead-qr-caption">Scan Verifikasi Keaslian Kartu</span>
              </div>

              <div className="ead-exam-sig-col">
                <p className="ead-exam-sig-title">Panitia PPDB Sekolah,</p>
                <div className="ead-exam-sig-space">
                  <div className="ead-sig-stamp">
                    PANITIA PPDB<br />PERMATA<br />HARAPAN
                  </div>
                </div>
                <div className="ead-exam-sig-line" />
                <p className="ead-exam-sig-name">{officerName}</p>
                <small className="ead-exam-sig-note">Panitia Penerimaan Peserta Didik Baru</small>
              </div>
            </div>

            {/* Footer */}
            <div className="ead-footer-note" style={{ marginTop: 12 }}>
              Dokumen resmi sistem PPDB Sekolah Permata Harapan. Harap disimpan dengan baik dan dibawa saat tes seleksi berlangsung.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
